import { formatNumber } from '@angular/common';
import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { of, Subscription } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import { CustomerProject } from '../../customer-project.interface';
import { AuthorisationService } from '../../services/auth/authorisation.service';
import { Functionalities } from '../../services/auth/functionalities';
import { HectometreService } from '../../services/hectometre/hectometre.service';
import { Workarea } from '../../workarea.class';

@Component({
    selector: 'app-workarea-naw',
    templateUrl: './workarea-naw.component.html',
    standalone: false,
})
export class WorkareaNawComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild('workareaForm', { static: true }) workareaForm: FormGroup;
    @Output()
    formValid = new EventEmitter<FormGroup>();
    @Input()
    workarea: Workarea;
    userIsContractor = false;
    @Input()
    projects: CustomerProject[];
    roadNumberControl = new FormControl('');
    roadNumberOptions: Array<{
        label: string;
        waynumber: string;
        hectometre: number;
        direction?: string | null;
        letter?: string | null;
    }> = [];
    private roadNumberSubscription: Subscription | null = null;

    constructor(private hectometreService: HectometreService) {}

    @HostListener('change', ['$event'])
    inputChanged(event) {
        this.formValid.emit(this.workareaForm);
    }

    ngOnInit() {
        // Derde partij: mag werkgebieden wel inzien, maar niet beheren.
        this.userIsContractor =
            AuthorisationService.hasAnyFunctionality([Functionalities.GeoRead, Functionalities.GeoRestricted]) &&
            !AuthorisationService.hasFunctionality(Functionalities.GeoManage);
        this.formValid.emit(this.workareaForm);
        this.roadNumberControl.setValue(this.workarea?.road_number ?? '');

        this.roadNumberSubscription = this.roadNumberControl.valueChanges
            .pipe(
                debounceTime(300),
                switchMap(value => {
                    if (!this.workarea) {
                        this.roadNumberOptions = [];
                        return of([]);
                    }

                    if (typeof value === 'string') {
                        this.workarea.road_number = value;
                    }

                    const query = typeof value !== 'object' ? `${value}`.trim() : '';
                    if (!query) {
                        this.roadNumberOptions = [];
                        return of([]);
                    }

                    return this.hectometreService.search(query).pipe(
                        map(result => {
                            const options = [];
                            result.data.forEach(rs => {
                                const hectometreFormatted = formatNumber(rs.hectometre / 10, 'nl', '1.1-1');
                                const directionOrLetter = rs.direction || rs.letter || '';
                                options.push({
                                    label: `${rs.waynumber}, ${hectometreFormatted} ${directionOrLetter}`.trim(),
                                    waynumber: rs.waynumber,
                                    hectometre: rs.hectometre,
                                    direction: rs.direction,
                                    letter: rs.letter,
                                });
                            });
                            return options;
                        })
                    );
                })
            )
            .subscribe(options => (this.roadNumberOptions = options));
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.workarea && this.workarea) {
            const currentValue = this.roadNumberControl.value;
            if (typeof currentValue === 'string' && currentValue !== this.workarea.road_number) {
                this.roadNumberControl.setValue(this.workarea.road_number ?? '', { emitEvent: false });
            }
        }
    }

    ngOnDestroy() {
        this.roadNumberSubscription?.unsubscribe();
    }

    projectChange(event) {
        this.projects.forEach(customerProject => {
            if (customerProject.id == this.workarea.customer_project_id) {
                this.workarea.customerProject = customerProject;
            }
        });
    }

    displayRoadNumberOption(option: { label: string } | string): string {
        if (!option) {
            return '';
        }
        if (typeof option === 'string') {
            return option;
        }
        return option.label;
    }

    onRoadNumberSelected(option: { waynumber: string }): void {
        if (option?.waynumber) {
            this.workarea.road_number = option.waynumber;
        }
    }
}
